import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Divider,
  GridItem,
  HStack,
  Link,
  SimpleGrid,
  Spacer,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode, useCallback } from 'react';
import {
  URL_ANTAA_FACEBOOK,
  URL_ANTAA_TWITTER,
  URL_CORPORATE_PRIVACY_POLICY,
  URL_CORPORATE_TERMS,
  URL_CORPORATE_TOP,
  URL_STORE_APPLE,
  URL_STORE_GOOGLE,
} from '../common/constants';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { GlobalNavArea } from './GlobalNavArea';

function FooterLink({
  href,
  children,
  isExternal = true,
}: {
  href: string;
  children: string;
  isExternal?: boolean;
}) {
  return (
    <Link
      display="inline-block"
      pr={4}
      py={1}
      href={href}
      color="textSecondary"
      fontSize="xs"
      isExternal={isExternal}
    >
      {children}
      <ChevronRightIcon color="brand.company" />
    </Link>
  );
}

function FooterLinkBox({
  href,
  children,
  isExternal = true,
}: {
  href: string;
  children: string;
  isExternal?: boolean;
}) {
  return (
    <Box>
      <FooterLink href={href} isExternal={isExternal}>
        {children}
      </FooterLink>
    </Box>
  );
}

function FooterSocialIconBox({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  return (
    <Box>
      <Link
        display="inline-block"
        px={2}
        py={1}
        mx={1}
        mt={2}
        href={href}
        color="footerText"
        fontSize="xs"
        isExternal
      >
        {children}
      </Link>
    </Box>
  );
}

function Footer() {
  const router = useRouter();
  const { colorMode } = useColorMode();
  const downloadBarHeight =
    typeof window !== 'undefined'
      ? document.getElementById('mobile-app-download-bar')?.offsetHeight
      : 0;
  const footerPaddingBottom = downloadBarHeight ? downloadBarHeight : 0;

  const sendShowGlobalNavEvent = useCallback(() => {
    sendAnalyticsEvent('globalnav', 'impression', null);
  }, []);
  const globalNavRef = useIntersectionObserver(sendShowGlobalNavEvent);

  const showGlobalNavPaths = [
    '/',
    '/articles',
    '/weekly-ranking-articles',
    '/mypage/like/articles',
    '/mypage/like/pages',
    '/mypage/view-history',
  ];
  const shouldShowGlobalNav = showGlobalNavPaths.includes(router.pathname);

  return (
    <Box
      as="footer"
      bg="backgroundMedium"
      px={4}
      py={[4, 4, 6]}
      mb={footerPaddingBottom}
      w="full"
    >
      <Container px={[0, 4]}>
        {shouldShowGlobalNav && (
          <Box ref={globalNavRef}>
            <GlobalNavArea />
          </Box>
        )}
        <SimpleGrid
          columns={[1, 1, 10]}
          alignItems={'center'}
          maxW={'668px'}
          marginX={'auto'}
          mb={4}
          gap={4}
        >
          <GridItem width={['auto', 'auto', 'fit-content']} colSpan={3}>
            <NextLink href="https://slide.antaa.jp" passHref>
              <Link
                display="flex"
                translateY="2px"
                transform="auto"
                flexShrink={0}
                justifyContent={'center'}
                onClick={() =>
                  sendAnalyticsEvent('link_to', 'slide', 'from_footer')
                }
              >
                <NextImage
                  src={
                    colorMode === 'light'
                      ? '/images/logo_slide_without_antaa_logo.png'
                      : '/images/logo_slide_white_large.png'
                  }
                  alt="Antaa Slide"
                  width={155}
                  height={40}
                  objectFit="contain"
                />
              </Link>
            </NextLink>
            <Text fontSize="x-small" color="textSecondary" textAlign={'center'}>
              医師・医学生のためのスライド共有
            </Text>
          </GridItem>
          <GridItem width={['auto', 'auto', 'fit-content']} colSpan={3}>
            <NextLink href="https://qa.antaa.jp" passHref>
              <Link
                display="flex"
                translateY="2px"
                transform="auto"
                flexShrink={0}
                justifyContent={'center'}
                onClick={() =>
                  sendAnalyticsEvent('link_to', 'qa', 'from_footer')
                }
              >
                <NextImage
                  src={
                    colorMode === 'light'
                      ? '/images/logo_qa.png'
                      : '/images/logo_qa_white.png'
                  }
                  alt="Antaa QA"
                  width={136}
                  height={40}
                  objectFit="contain"
                />
              </Link>
            </NextLink>
            <Text fontSize="x-small" color="textSecondary" textAlign={'center'}>
              医師同士の質問解決プラットフォーム
            </Text>
          </GridItem>
          <GridItem width={['auto', 'auto', 'fit-content']} colSpan={4}>
            <HStack justifyContent={'center'} gap={4}>
              <Link href={URL_STORE_APPLE} target="_blank">
                <NextImage
                  src="/images/store_ios.svg"
                  alt="App Storeからダウンロード"
                  width={110}
                  height={40}
                  objectFit="contain"
                />
              </Link>
              <Link href={URL_STORE_GOOGLE} target="_blank">
                <NextImage
                  src="/images/store_android.png"
                  alt="Google Play Storeからダウンロード"
                  width={135}
                  height={40}
                  objectFit="contain"
                />
              </Link>
            </HStack>
          </GridItem>
        </SimpleGrid>

        <Divider mb={4} color={'borderLight'} opacity={1} />
        <Box display={['block', 'block', 'flex', 'flex']} alignItems="flex-end">
          <FooterLinkBox href={URL_CORPORATE_TOP}>会社概要</FooterLinkBox>
          <FooterLinkBox href={URL_CORPORATE_TERMS}>利用規約</FooterLinkBox>
          <FooterLinkBox href={URL_CORPORATE_PRIVACY_POLICY}>
            プライバシーポリシー
          </FooterLinkBox>
          <FooterLinkBox href="/docs/community-guidelines" isExternal={false}>
            Antaaガイドライン
          </FooterLinkBox>
          <FooterLinkBox href="/docs/ip-guideline" isExternal={false}>
            知的財産取扱いガイドライン
          </FooterLinkBox>
          <FooterLinkBox href="/docs/faq/index.html">FAQ</FooterLinkBox>
          <Spacer />
          <Box
            display="flex"
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Text
              fontSize="x-small"
              color="textSecondary"
              py={1}
              pt={[3, 3, null, null]}
            >
              Antaa, Inc. All rights reserved.
            </Text>
            <Box display="flex">
              <FooterSocialIconBox href={URL_ANTAA_FACEBOOK}>
                <NextImage
                  src="/images/facebook_icon.png"
                  alt="Follow us on Facebook"
                  width={30}
                  height={30}
                  objectFit="contain"
                />
              </FooterSocialIconBox>
              <FooterSocialIconBox href={URL_ANTAA_TWITTER}>
                <NextImage
                  src="/images/x_twitter_icon.png"
                  alt="Follow us on Twitter"
                  width={26}
                  height={30}
                  objectFit="contain"
                />
              </FooterSocialIconBox>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
